import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { TERMS_AND_CONDITIONS_CHECKBOX_NAME } from 'components/associated-dealers/constants/constants';
import { TERMS_LINKS } from 'constants/links';
import useGetDealerInfo from 'components/dealer-locator/hooks/use-get-dealer-info';
import { useEffect } from 'react';
import { TermsAndConditionsCheckbox } from './terms-and-conditions-checkbox';
import { CAT_LEGAL_TERMS_FLAG } from 'constants/featureFlag';
import { useParams } from 'next/navigation';
import { getCatOrDealerLegalURL } from 'utils/url';

export const TermsAndConditions = ({
  selectedDealer,
  dealerUserResponse,
  isDealerUser
}) => {
  const t = useTranslations();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es').
  const { lang }: esiteParams = useParams();

  const { getDealerInfo, data } = useGetDealerInfo();
  const dealerName = selectedDealer?.dealerName || dealerUserResponse?.name;
  const isTermsAndConditionsAccepted =
    selectedDealer?.isDealerTermsAndConditionAcceptedViaWeb;
  const showTerms =
    (dealerName !== undefined && !isTermsAndConditionsAccepted) || isDealerUser;

  const dealerStoreId = selectedDealer?.id || dealerUserResponse?.storeId;
  const catLegalTermsFlagValue =
    data?.featureFlags?.[CAT_LEGAL_TERMS_FLAG] ?? false;
  const privacyNoticeURL = catLegalTermsFlagValue
    ? getCatOrDealerLegalURL(TERMS_LINKS.CAT_PRIVACY_NOTICE_LINK, lang, true)
    : getCatOrDealerLegalURL(TERMS_LINKS.CAT_PRIVACY_NOTICE, lang, true);
  const legalTermsURL = catLegalTermsFlagValue
    ? getCatOrDealerLegalURL(TERMS_LINKS.CAT_LEGAL_TERMS_LINK, lang, true)
    : getCatOrDealerLegalURL(TERMS_LINKS.CAT_LEGAL_TERMS_OF_USE, lang, true);

  useEffect(() => {
    if (dealerStoreId) {
      getDealerInfo(dealerStoreId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerStoreId]);

  const defaultDealersTermsOfUse = replaceTokensWithComponents(
    t('DEALERS_TERMS_OF_USE', { '0': '{0}' }),
    <span>{dealerName}</span>
  );
  const defaultDealersPrivacyNotice = replaceTokensWithComponents(
    t('UR_CAT_DEALER_PRIVACY_NOTICE_HREF', { '0': '{0}' }),
    <span>{dealerName}</span>
  );

  const defaultTermsAndConditions = replaceTokensWithComponents(
    t('TERMS_CONDITIONS_PARTSCAT_ADP', {
      '0': '{0}',
      '1': '{1}',
      '2': '{2}',
      '3': '{3}'
    }),
    <Link href={legalTermsURL} target="_blank">
      {t('UR_PARTS_TERMS_OF_USE_HREF')}
    </Link>,
    <Link href={privacyNoticeURL} target="_blank">
      {t('UR_CAT_PRIVACY_NOTICE_HREF')}
    </Link>,
    <Link href={`${data?.seoURL}${TERMS_LINKS.DEALER_TERMS}`} target="_blank">
      {defaultDealersTermsOfUse}
    </Link>,
    <Link
      href={`${data?.seoURL}${TERMS_LINKS.DEALER_PRIVACY_POLICY}`}
      target="_blank"
    >
      {defaultDealersPrivacyNotice}
    </Link>
  );

  return (
    <TermsAndConditionsCheckbox
      dealerName={selectedDealer?.dealerName}
      name={TERMS_AND_CONDITIONS_CHECKBOX_NAME}
      label={t('1628_ACCEPT_TERMS_AND_CONDITIONS_2')}
      fieldNote={defaultTermsAndConditions}
      checked={isTermsAndConditionsAccepted}
      showCheckbox={showTerms}
      required={true}
    />
  );
};
